import type { Schema } from 'effect';

import type { BackendApiErrorUnion } from '../../data/error/backendApi/_internal/createBackendApiError';
import type { BrandAlphabetAdapters } from './brandAdapters';
import { makeListBrands } from '../../request/backendApi/brand/makeListBrands';
import { makeListBrandsAlphabet } from '../../request/backendApi/brand/makeListBrandsAlphabet';
import type { StripJsonLd } from '@vs-shop/schemas/transform/withoutJsonLd';

/** Engine-side brand module, composed from its original request factories. */
export type BrandModuleDeps<BrandAlphabet extends Schema.Top, BrandIndex extends Schema.Top> = {
  readonly brandAlphabet: BrandAlphabet;
  readonly brandAlphabetAdapters: BrandAlphabetAdapters<StripJsonLd<BrandAlphabet['Type']>>;
  readonly brandIndex: BrandIndex;
};

export const makeBrandModule = <BrandAlphabet extends Schema.Top, BrandIndex extends Schema.Top>(
  deps: BrandModuleDeps<BrandAlphabet, BrandIndex>
) => {
  const brandAlphabet = makeListBrandsAlphabet({
    brandAlphabet: deps.brandAlphabet,
    brandAlphabetAdapters: deps.brandAlphabetAdapters,
  });
  const brands = makeListBrands({ brandIndex: deps.brandIndex });

  return {
    listBrandsAlphabet: brandAlphabet.listBrandsAlphabet,
    ListBrandsAlphabetError: brandAlphabet.ListBrandsAlphabetError,
    getBrands: brands.getBrands,
    requestBrands: brands.requestBrands,
    ListBrandsError: brands.ListBrandsError,
  };
};

export type BrandListAlphabetError = BackendApiErrorUnion<
  ReturnType<typeof makeBrandModule>['ListBrandsAlphabetError']
>;
export type BrandListError = BackendApiErrorUnion<
  ReturnType<typeof makeBrandModule>['ListBrandsError']
>;
